import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import {
  EditOutlined,
  ManageAccountsOutlined,
  MoreVert,
  PictureAsPdfOutlined,
  TableChartOutlined,
} from '@mui/icons-material';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Company } from 'api';
import { baseCompanyUrl } from 'common/config/urls';
import { CAP_TABLE_SLUG } from 'common/constants/cap-table';
import { MISSING_MD_MESSAGE, MISSING_MD_TITLE } from 'common/constants/messages/validations';
import { EDIT_COMMON_PROFILE, EDIT_COMPANY, EXCEL_EXPORT, PDF_EXPORT } from 'common/constants/pageActions';
import { COMPANY_TYPE } from 'common/constants/pageTypes';
import { AUDITOR_VALUE, EXPORT_VIEWER_VALUE, FULL_ACCESS_VIEWER_VALUE } from 'common/constants/user';
import { useExtraPageActionVisibility, useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import { ExtraAction, NavItems, UseStoreValues } from 'common/types/store';
import { DrawerBottomSpacer } from 'components/Drawer/draggable-drawer/DrawerBottomSpacer';
import EditCommonProfileDialog from 'components/EditCommonProfileDialog';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import MissingMeasurementDateMessage from 'components/MissingMeasurementDateMessage';
import { PdfExportConfigDialog } from 'components/PdfExportConfigDialog';
import { LoadingState, PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import withCompany from 'components/WithCompany';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { useLayoutContext } from 'context/LayoutContext';
import { ExcelExportDialogV2 } from 'layouts/Main/components/ExcelExportDialogV2';
import NewCompanyDialog from 'layouts/Main/components/NewCompanyDialog';
import {
  CreateCapTableCitationsDialog,
  CreateCitationsMenuItem,
  useCreateCapTableCitationsAction,
} from 'pages/CapTable/cap-table/components/create-citations';
import { extractDate, removeParamFromQueryParams } from 'utilities';
import { useUserUtils } from 'utilities/userUtils';
import {
  buildCompanyNavigationUrl,
  COMPANY_NAVIGATION_ROUTES,
  COMPANY_SECONDARY_NAVIGATION_LOADING_ARIA_LABEL,
  type CompanyNavigationRoute,
} from './companyNavigation';
import { getCompanyRouteView } from './companyRouteViews';

const MORE_VERT_ICON = <MoreVert />;
const VIEW_ONLY_EXCEL_EXPORT_ROLES = [FULL_ACCESS_VIEWER_VALUE, EXPORT_VIEWER_VALUE, AUDITOR_VALUE];

const CompanyContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companySlugParam, firmSlugParam, tableSlugParam } = useParams();
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo } = storeValues;
  const {
    setPageTitle,
    setNavItems,
    setShowAlert,
    setAlertMessage,
    setCommonMeasurementDate,
    toggleMainForm,
    toggleExportDialog,
    togglePdfDialog,
    openPdfDialog,
    updateExtraPageActions,
    setPageType,
  } = useLayoutContext();
  const [, checkFeature] = useCheckFeatures();
  const { canEditCompany, currentFirmRole, isViewOnlyUser } = useFirmScopedPermissions();
  const { isStaffUser, isSuperUser } = useUserUtils();
  const canAccessStaffActions = isStaffUser() || isSuperUser();
  const actionVisibility = useExtraPageActionVisibility({
    createCitations: tableSlugParam === CAP_TABLE_SLUG,
    pdfExport: canAccessStaffActions,
  });
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [editCompanyOpen, setEditCompanyOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const commonProfileId = companyInfo?.common_profile_id as number | null | undefined;
  const commonProfileConfirmed = companyInfo?.common_profile_confirmed as boolean | null | undefined;
  const isProfileUnconfirmed = commonProfileId != null && commonProfileConfirmed === false;

  const firmInfoSlug = firmInfo?.slug ?? '';
  const companyInfoSlug = companyInfo?.slug ?? '';

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search]);

  const handleMoreActionsOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMoreActionsAnchorEl(event.currentTarget);
  }, []);

  const handleMoreActionsClose = useCallback(() => {
    setMoreActionsAnchorEl(null);
  }, []);

  const handleEditCompanyClick = useCallback(() => {
    if (!canEditCompany) {
      return;
    }

    handleMoreActionsClose();
    setEditCompanyOpen(true);
  }, [canEditCompany, handleMoreActionsClose]);

  const handleExportClick = useCallback(() => {
    handleMoreActionsClose();
    setExportDialogOpen(true);
  }, [handleMoreActionsClose]);

  const handlePdfExportClick = useCallback(() => {
    handleMoreActionsClose();
    togglePdfDialog?.();
  }, [handleMoreActionsClose, togglePdfDialog]);

  const handleEditCommonProfileClick = useCallback(() => {
    handleMoreActionsClose();
    setEditProfileOpen(true);
  }, [handleMoreActionsClose]);

  const handleEditCompanyClose = useCallback(
    (updatedCompany?: Company | null) => {
      setEditCompanyOpen(false);

      if (!updatedCompany?.slug || !firmSlugParam || updatedCompany.slug === companySlugParam) {
        return;
      }

      const nextTableSlug = tableSlugParam ?? '';
      navigate(`${baseCompanyUrl(firmSlugParam, updatedCompany.slug)}/${nextTableSlug}${location.search}`, {
        replace: true,
      });
    },
    [companySlugParam, firmSlugParam, location.search, navigate, tableSlugParam]
  );

  // Company route params can update before the legacy store bridge hydrates the matching firm/company.
  const isCompanyRouteHydrating =
    (Boolean(firmSlugParam) && firmSlugParam !== firmInfoSlug)
    || (Boolean(companySlugParam) && companySlugParam !== companyInfoSlug);
  const isCompanyContextLoading =
    isEmpty(firmInfo) || isEmpty(companyInfo) || !companyInfo?.id || isCompanyRouteHydrating;
  const hasCompanyRouteContext = !isCompanyContextLoading && Boolean(firmInfoSlug && companyInfoSlug);
  const isMissingMeasurementDate =
    !isCompanyContextLoading && !isEmpty(companyInfo) && !companyInfo?.measurement_date_slug;

  // Cap-table-only page action; without a measurement date there is no cap table to cite.
  const canShowCreateCitations = actionVisibility.createCitations && !isMissingMeasurementDate && !isViewOnlyUser;
  const createCitationsAction = useCreateCapTableCitationsAction({ enabled: canShowCreateCitations });
  const { openDialog: openCreateCitationsDialog } = createCitationsAction;
  const handleCreateCitationsClick = useCallback(() => {
    handleMoreActionsClose();
    openCreateCitationsDialog();
  }, [handleMoreActionsClose, openCreateCitationsDialog]);

  // Sync the shared measurement date from the URL when cross-company navigation carries a date query.
  useEffect(() => {
    if (setCommonMeasurementDate && parsedQuery?.date && typeof parsedQuery.date === 'string') {
      const extractedDate = extractDate({
        dateSlug: parsedQuery.date,
      });
      if (extractedDate) {
        setCommonMeasurementDate(extractedDate);
      }
    }
  }, [parsedQuery?.date, setCommonMeasurementDate]);

  // Keep the legacy LayoutContext page type in sync for company-owned children still reading it.
  useEffect(() => {
    setPageType?.(COMPANY_TYPE);
    return () => {
      setPageType?.(null);
    };
  }, [setPageType]);

  const getNavigationUrl = useCallback(
    (route: CompanyNavigationRoute) => {
      if (!hasCompanyRouteContext) {
        return null;
      }

      return buildCompanyNavigationUrl({
        companySlug: companyInfoSlug,
        currentTableSlug: tableSlugParam,
        firmSlug: firmInfoSlug,
        getUrl: route.route,
        queryParams: parsedQuery,
        targetTableSlug: route.slug,
      });
    },
    [companyInfoSlug, firmInfoSlug, hasCompanyRouteContext, parsedQuery, tableSlugParam]
  );

  const navigateToUrl = useCallback(
    (route: CompanyNavigationRoute) => {
      const navigationUrl = getNavigationUrl(route);

      if (navigationUrl) {
        navigate(navigationUrl);
      }
    },
    [getNavigationUrl, navigate]
  );

  const visibleCompanyRoutes = useMemo(
    () =>
      COMPANY_NAVIGATION_ROUTES.filter(route =>
        !route.hidden && (route.featureFlag ? checkFeature(route.featureFlag) : true)
      ),
    [checkFeature]
  );

  const menuItems = useMemo(
    (): NavItems[] =>
      visibleCompanyRoutes.map(route => ({
        ...route,
        callback: () => navigateToUrl(route),
      })),
    [navigateToUrl, visibleCompanyRoutes]
  );

  const secondaryNavigationItems = useMemo(
    () =>
      visibleCompanyRoutes.map(route => ({
        label: route.title,
        to: getNavigationUrl(route) ?? undefined,
        value: route.slug,
        ...(route.alsoActiveFor && hasCompanyRouteContext
          ? { activeFor: route.alsoActiveFor.map(builder => builder(firmInfoSlug, companyInfoSlug)) }
          : {}),
      })),
    [getNavigationUrl, visibleCompanyRoutes, hasCompanyRouteContext, firmInfoSlug, companyInfoSlug]
  );

  const hasFirmExportContext = currentFirmRole !== null || canAccessStaffActions;
  const canShowExcelExport = actionVisibility.excelExport
    && !isMissingMeasurementDate
    && hasFirmExportContext
    && (!isViewOnlyUser || VIEW_ONLY_EXCEL_EXPORT_ROLES.includes(currentFirmRole as number));
  const canShowPdfExport = actionVisibility.pdfExport && !isMissingMeasurementDate && !isViewOnlyUser;
  const canShowEditCommonProfile = canAccessStaffActions && commonProfileId != null;
  const hasCompanyMoreActions =
    canEditCompany || canShowExcelExport || canShowPdfExport || canShowEditCommonProfile || canShowCreateCitations;

  const secondaryNavigation = useMemo(() => {
    if (isCompanyContextLoading) {
      return {
        ariaLabel: COMPANY_SECONDARY_NAVIGATION_LOADING_ARIA_LABEL,
        isLoading: true,
        skeletonProps: {
          itemCount: visibleCompanyRoutes.length,
          showActions: false,
          showStatus: false,
        },
        title: 'Company',
      };
    }

    if (isEmpty(companyInfo) || !companyInfo?.name) {
      return null;
    }

    return {
      actions: hasCompanyMoreActions
        ? [
          {
            ariaLabel: 'More actions',
            icon: MORE_VERT_ICON,
            onClick: handleMoreActionsOpen,
            type: 'iconButton' as const,
          },
        ]
        : [],
      items: secondaryNavigationItems,
      title: companyInfo.name,
    };
  }, [
    companyInfo,
    handleMoreActionsOpen,
    hasCompanyMoreActions,
    isCompanyContextLoading,
    secondaryNavigationItems,
    visibleCompanyRoutes.length,
  ]);

  useSecondaryNavigationRegistration(secondaryNavigation);

  // Clear legacy alert state when navigating away from this company route.
  useEffect(
    () => () => {
      setShowAlert?.(false);
      setAlertMessage?.('');
    },
    [location.pathname, setAlertMessage, setShowAlert]
  );

  // Bridge the active company title/nav into legacy LayoutContext for old consumers.
  useEffect(() => {
    if (!isEmpty(companyInfo) && companyInfo.name) {
      setPageTitle?.(companyInfo.name);
      setNavItems?.(menuItems);
    }

    return () => {
      setPageTitle?.(null);
      setNavItems?.(null);
    };
  }, [companyInfo, menuItems, setNavItems, setPageTitle]);

  // Keep legacy page actions aligned with the active company; exports require a real measurement date.
  useEffect(() => {
    const extraActions: ExtraAction[] = [];

    if (canEditCompany) {
      extraActions.push({
        label: EDIT_COMPANY,
        callback: toggleMainForm,
        isActive: true,
      });
    }

    if (canShowExcelExport) {
      extraActions.push({
        label: EXCEL_EXPORT,
        callback: toggleExportDialog,
        isActive: true,
      });
    }

    if (canShowPdfExport) {
      extraActions.push({
        label: PDF_EXPORT,
        callback: togglePdfDialog,
        isActive: true,
      });
    }

    if (canShowEditCommonProfile) {
      extraActions.push({
        label: EDIT_COMMON_PROFILE,
        callback: () => setEditProfileOpen(true),
        isActive: true,
      });
    }

    updateExtraPageActions?.(extraActions);
  }, [
    toggleMainForm,
    updateExtraPageActions,
    togglePdfDialog,
    toggleExportDialog,
    canEditCompany,
    canShowEditCommonProfile,
    canShowExcelExport,
    canShowPdfExport,
  ]);

  // A company without measurement dates cannot honor a carried `date`; clear it so
  // the missing-MD empty state owns the add-measurement-date path.
  useEffect(() => {
    if (isMissingMeasurementDate) {
      removeParamFromQueryParams('date');
    }
  }, [isMissingMeasurementDate]);

  if (isCompanyContextLoading) {
    return <LoadingState minHeight="calc(100vh - 160px)" />;
  }

  const companyContent = isMissingMeasurementDate ? (
    <MissingMeasurementDateMessage title={MISSING_MD_TITLE} tagline={MISSING_MD_MESSAGE} />
  ) : (
    <DrawerBottomSpacer>
      {getCompanyRouteView(tableSlugParam)}
    </DrawerBottomSpacer>
  );

  return (
    <>
      {companyContent}
      <PdfExportConfigDialog
        open={openPdfDialog}
        onClose={togglePdfDialog}
      />
      {editProfileOpen && commonProfileId != null && (
        <EditCommonProfileDialog
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
          profileId={commonProfileId}
          companyId={companyInfo?.id}
          confirmationPending={isProfileUnconfirmed}
        />
      )}
      <NewCompanyDialog
        editMode
        open={editCompanyOpen}
        onClose={handleEditCompanyClose}
      />
      <ExcelExportDialogV2
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
      {canShowCreateCitations && (
        <CreateCapTableCitationsDialog
          documents={createCitationsAction.documents}
          onClose={createCitationsAction.closeDialog}
          open={createCitationsAction.isDialogOpen}
        />
      )}
      <PrimaryNavigationMenu
        anchorEl={moreActionsAnchorEl}
        menuMinWidth={0}
        menuWidth="content"
        onClose={handleMoreActionsClose}
        open={Boolean(moreActionsAnchorEl)}>
        {canEditCompany && (
          <PrimaryNavigationMenuItem icon={<EditOutlined fontSize="inherit" />} onClick={handleEditCompanyClick}>
            {EDIT_COMPANY}
          </PrimaryNavigationMenuItem>
        )}
        {canShowExcelExport && (
          <PrimaryNavigationMenuItem
            icon={<TableChartOutlined fontSize="inherit" />}
            onClick={handleExportClick}>
            {EXCEL_EXPORT}
          </PrimaryNavigationMenuItem>
        )}
        {canShowPdfExport && (
          <PrimaryNavigationMenuItem
            icon={<PictureAsPdfOutlined fontSize="inherit" />}
            onClick={handlePdfExportClick}>
            {PDF_EXPORT}
          </PrimaryNavigationMenuItem>
        )}
        {canShowCreateCitations && (
          <CreateCitationsMenuItem
            disabledReason={createCitationsAction.disabledReason}
            onClick={handleCreateCitationsClick}
          />
        )}
        {canShowEditCommonProfile && (
          <PrimaryNavigationMenuItem
            icon={<ManageAccountsOutlined fontSize="inherit" />}
            onClick={handleEditCommonProfileClick}>
            {EDIT_COMMON_PROFILE}
          </PrimaryNavigationMenuItem>
        )}
      </PrimaryNavigationMenu>
    </>
  );
};

export default withCompany(CompanyContainer);
